/**
 * Radio Command Hooks
 *
 * TanStack Query hooks for stun/unstun operations
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '../client';
import type { CommandResponse, CommandStatusResponse } from '../types/commands';
import { equipmentKeys } from './useEquipment';

/** Query keys for commands */
export const commandKeys = {
  all: ['commands'] as const,
  detail: (id: string) => [...commandKeys.all, id] as const,
};

/**
 * Stun a radio
 */
export function useStunRadio() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (radioId: string) =>
      apiFetch<CommandResponse>(`/api/radio/${radioId}/stun`, {
        method: 'POST',
      }),
    onSuccess: () => {
      // The actual status update will come via SSE
      // Optimistic update could be added here
      queryClient.invalidateQueries({ queryKey: equipmentKeys.lists() });
    },
  });
}

/**
 * Unstun a radio
 */
export function useUnstunRadio() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (radioId: string) =>
      apiFetch<CommandResponse>(`/api/radio/${radioId}/unstun`, {
        method: 'POST',
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: equipmentKeys.lists() });
    },
  });
}

/**
 * Check command status
 */
export function useCommandStatus(commandId: string | undefined) {
  return useQuery({
    queryKey: commandKeys.detail(commandId ?? ''),
    queryFn: () => apiFetch<CommandStatusResponse>(`/api/commands/${commandId}`),
    enabled: !!commandId,
    refetchInterval: (query) => {
      // Poll while command is pending/processing
      const status = query.state.data?.status;
      if (status === 'pending' || status === 'processing') {
        return 2000; // Poll every 2 seconds
      }
      return false; // Stop polling when complete
    },
  });
}
